const measurers = document.querySelectorAll('[data-roofline-measurer]');

for (const root of measurers) mountRooflineMeasurer(root);

function mountRooflineMeasurer(root) {
  if (root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const $ = (selector) => root.querySelector(selector);
  const uploadView = $('[data-upload-view]');
  const workspace = $('[data-workspace]');
  const photoInput = $('[data-photo-input]');
  const uploadMessage = $('[data-upload-message]');
  const stage = $('[data-stage]');
  const image = $('[data-house-image]');
  const drawingLayer = $('[data-drawing-layer]');
  const stageHint = $('[data-stage-hint]');
  const stageStatus = $('[data-stage-status]');
  const referenceInput = $('[data-reference-value]');
  const referenceUnit = $('[data-reference-unit]');
  const referencePanel = $('[data-reference-panel]');
  const depthInput = $('[data-depth-value]');
  const depthUnit = $('[data-depth-unit]');
  const depthPanel = $('[data-depth-panel]');
  const traceScalePanel = $('[data-trace-scale]');
  const totalPrimary = $('[data-total-primary]');
  const totalSecondary = $('[data-total-secondary]');
  const runList = $('[data-run-list]');
  const cameraDialog = $('[data-camera-dialog]');
  const cameraVideo = $('[data-camera-video]');
  const confirmDialog = $('[data-confirm-dialog]');

  const state = {
    imageWidth: 0,
    imageHeight: 0,
    mode: 'reference',
    referencePoints: [],
    referenceValue: 0,
    referenceUnit: 'ft',
    depthPoints: [],
    depthValue: 0,
    depthUnit: 'ft',
    activeScale: 'front',
    segments: [],
    activeSegmentId: null,
    selected: null
  };

  let history = [];
  let cameraStream = null;
  let dragging = null;

  function createId(prefix) {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }

  function track(action, details = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'roofline_measurer', measurer_action: action, ...details });
  }

  function distance(left, right) {
    const x = (right.x - left.x) * state.imageWidth;
    const y = (right.y - left.y) * state.imageHeight;
    return Math.hypot(x, y);
  }

  function polylineDistance(points) {
    let total = 0;
    for (let index = 1; index < points.length; index += 1) total += distance(points[index - 1], points[index]);
    return total;
  }

  function referenceData(scale = 'front') {
    return scale === 'depth'
      ? { points: state.depthPoints, value: state.depthValue, unit: state.depthUnit }
      : { points: state.referencePoints, value: state.referenceValue, unit: state.referenceUnit };
  }

  function calibration(scale = 'front') {
    const reference = referenceData(scale);
    if (reference.points.length !== 2 || !(reference.value > 0)) return null;
    const pixels = distance(reference.points[0], reference.points[1]);
    if (!(pixels > 1)) return null;
    const metres = reference.unit === 'ft' ? reference.value * 0.3048 : reference.value;
    return { pixels, metres, metresPerPixel: metres / pixels };
  }

  function formatLength(metres, unit = state.referenceUnit) {
    const value = unit === 'ft' ? metres / 0.3048 : metres;
    const suffix = unit === 'ft' ? 'ft' : 'm';
    return `${value.toFixed(value < 100 ? 1 : 0)} ${suffix}`;
  }

  function measuredSegments() {
    return state.segments
      .filter((segment) => segment.points.length > 1)
      .map((segment) => {
        const scale = calibration(segment.scale || 'front');
        return scale ? { ...segment, metres: polylineDistance(segment.points) * scale.metresPerPixel } : null;
      })
      .filter(Boolean);
  }

  function snapshot() {
    return JSON.stringify({
      referencePoints: state.referencePoints,
      depthPoints: state.depthPoints,
      segments: state.segments,
      activeSegmentId: state.activeSegmentId,
      activeScale: state.activeScale
    });
  }

  function pushHistory() {
    const next = snapshot();
    if (history[history.length - 1] !== next) history.push(next);
    if (history.length > 80) history.shift();
    updateUI();
  }

  function undo() {
    if (history.length < 2) return;
    history.pop();
    const previous = JSON.parse(history[history.length - 1]);
    state.referencePoints = previous.referencePoints || [];
    state.depthPoints = previous.depthPoints || [];
    state.segments = previous.segments || [];
    state.activeSegmentId = previous.activeSegmentId || null;
    state.activeScale = previous.activeScale || 'front';
    state.selected = null;
    render();
  }

  function stagePoint(event) {
    const rect = drawingLayer.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / Math.max(1, rect.width))),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / Math.max(1, rect.height)))
    };
  }

  function activeSegment() {
    return state.segments.find((segment) => segment.id === state.activeSegmentId) || null;
  }

  function createSegment() {
    const segment = { id: createId('run'), points: [], scale: state.activeScale };
    state.segments.push(segment);
    state.activeSegmentId = segment.id;
    return segment;
  }

  function addPoint(event) {
    const point = stagePoint(event);
    if (state.mode === 'reference') {
      if (state.referencePoints.length >= 2) {
        stageStatus.textContent = 'The reference is already set. Drag either endpoint to adjust it, or choose Reset points.';
        return;
      }
      state.referencePoints.push(point);
      state.selected = { kind: 'reference', pointIndex: state.referencePoints.length - 1 };
      pushHistory();
      render();
      return;
    }
    if (state.mode === 'depth') {
      if (state.depthPoints.length >= 2) {
        stageStatus.textContent = 'The depth reference is already set. Drag either endpoint to adjust it, or choose Reset depth.';
        return;
      }
      state.depthPoints.push(point);
      state.selected = { kind: 'depth', pointIndex: state.depthPoints.length - 1 };
      pushHistory();
      render();
      return;
    }
    if (!calibration('front')) {
      setMode('reference');
      stageStatus.textContent = 'Finish the known-distance reference before tracing the roofline.';
      referenceInput.focus();
      return;
    }
    if (state.activeScale === 'depth' && !calibration('depth')) {
      setMode('depth');
      stageStatus.textContent = 'Finish the depth reference before tracing a depth-facing run.';
      depthInput.focus();
      return;
    }
    const segment = activeSegment() || createSegment();
    segment.points.push(point);
    state.selected = { kind: 'segment', segmentId: segment.id, pointIndex: segment.points.length - 1 };
    pushHistory();
    render();
  }

  function setMode(mode) {
    if (mode === 'trace' && !calibration('front')) {
      state.mode = 'reference';
      stageStatus.textContent = 'Enter the actual distance and mark both endpoints before tracing.';
      if (!(state.referenceValue > 0)) referenceInput.focus();
    } else {
      state.mode = mode;
    }
    state.selected = null;
    root.querySelectorAll('[data-mode-button]').forEach((button) => {
      const active = button.dataset.modeButton === state.mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });
    referencePanel.hidden = state.mode !== 'reference';
    depthPanel.hidden = state.mode !== 'depth';
    traceScalePanel.hidden = state.mode !== 'trace';
    render();
  }

  function finishRun(startAnother = false) {
    const segment = activeSegment();
    if (segment && segment.points.length < 2) {
      stageStatus.textContent = 'Add at least two points before finishing this run.';
      return;
    }
    if (segment) track('run_finished', { point_count: segment.points.length, scale: segment.scale || 'front' });
    state.activeSegmentId = null;
    state.selected = null;
    if (startAnother) createSegment();
    pushHistory();
    render();
  }

  function deleteSelectedPoint() {
    if (!state.selected) return;
    if (state.selected.kind === 'reference') {
      state.referencePoints.splice(state.selected.pointIndex, 1);
      state.segments = [];
      state.activeSegmentId = null;
    } else if (state.selected.kind === 'depth') {
      state.depthPoints.splice(state.selected.pointIndex, 1);
      state.segments = state.segments.filter((segment) => segment.scale !== 'depth');
      if (!state.segments.some((segment) => segment.id === state.activeSegmentId)) state.activeSegmentId = null;
    } else {
      const segment = state.segments.find((entry) => entry.id === state.selected.segmentId);
      if (segment) {
        segment.points.splice(state.selected.pointIndex, 1);
        if (!segment.points.length) {
          state.segments = state.segments.filter((entry) => entry.id !== segment.id);
          if (state.activeSegmentId === segment.id) state.activeSegmentId = null;
        }
      }
    }
    state.selected = null;
    pushHistory();
    render();
  }

  function svgElement(name, attributes = {}) {
    const element = document.createElementNS('http://www.w3.org/2000/svg', name);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
    return element;
  }

  function appendHandle(point, options) {
    const selected = state.selected
      && state.selected.kind === options.kind
      && state.selected.pointIndex === options.pointIndex
      && (options.kind !== 'segment' || state.selected.segmentId === options.segmentId);
    const handle = svgElement('circle', {
      cx: point.x * state.imageWidth,
      cy: point.y * state.imageHeight,
      r: Math.max(8, Math.min(state.imageWidth, state.imageHeight) * 0.012),
      class: `rmt-handle${options.kind === 'reference' ? ' rmt-reference-handle' : ''}${options.kind === 'depth' ? ' rmt-depth-handle' : ''}${options.scale === 'depth' ? ' rmt-depth-run-handle' : ''}${selected ? ' is-selected' : ''}`
    });
    handle.dataset.kind = options.kind;
    handle.dataset.pointIndex = String(options.pointIndex);
    if (options.segmentId) handle.dataset.segmentId = options.segmentId;
    drawingLayer.append(handle);
  }

  function appendReferenceLabel(scale = 'front') {
    const reference = referenceData(scale);
    if (reference.points.length !== 2) return;
    const [left, right] = reference.points;
    const x = ((left.x + right.x) / 2) * state.imageWidth;
    const y = ((left.y + right.y) / 2) * state.imageHeight - 28;
    const value = reference.value > 0 ? `${reference.value} ${reference.unit}` : scale === 'depth' ? 'Depth distance' : 'Front distance';
    const group = svgElement('g', { class: `rmt-measure-label${scale === 'depth' ? ' is-depth' : ''}`, transform: `translate(${x} ${y})` });
    group.append(svgElement('rect', { x: -72, y: -19, width: 144, height: 38, rx: 12 }));
    const text = svgElement('text', { x: 0, y: 1 });
    text.textContent = value;
    group.append(text);
    drawingLayer.append(group);
  }

  function renderDrawing() {
    if (!state.imageWidth || !state.imageHeight) return;
    drawingLayer.setAttribute('viewBox', `0 0 ${state.imageWidth} ${state.imageHeight}`);
    drawingLayer.replaceChildren();

    if (state.referencePoints.length > 1) {
      drawingLayer.append(svgElement('line', {
        x1: state.referencePoints[0].x * state.imageWidth,
        y1: state.referencePoints[0].y * state.imageHeight,
        x2: state.referencePoints[1].x * state.imageWidth,
        y2: state.referencePoints[1].y * state.imageHeight,
        class: 'rmt-reference-line'
      }));
    }
    state.referencePoints.forEach((point, pointIndex) => appendHandle(point, { kind: 'reference', pointIndex }));
    appendReferenceLabel('front');

    if (state.depthPoints.length > 1) {
      drawingLayer.append(svgElement('line', {
        x1: state.depthPoints[0].x * state.imageWidth,
        y1: state.depthPoints[0].y * state.imageHeight,
        x2: state.depthPoints[1].x * state.imageWidth,
        y2: state.depthPoints[1].y * state.imageHeight,
        class: 'rmt-reference-line rmt-depth-line'
      }));
    }
    state.depthPoints.forEach((point, pointIndex) => appendHandle(point, { kind: 'depth', pointIndex }));
    appendReferenceLabel('depth');

    state.segments.forEach((segment) => {
      if (segment.points.length > 1) {
        const polyline = svgElement('polyline', {
          points: segment.points.map((point) => `${point.x * state.imageWidth},${point.y * state.imageHeight}`).join(' '),
          class: `rmt-roofline${segment.scale === 'depth' ? ' is-depth' : ''}${segment.id === state.activeSegmentId ? ' is-selected' : ''}`
        });
        polyline.dataset.segmentLine = segment.id;
        drawingLayer.append(polyline);
      }
      segment.points.forEach((point, pointIndex) => appendHandle(point, { kind: 'segment', segmentId: segment.id, pointIndex, scale: segment.scale || 'front' }));
    });
  }

  function updateResults() {
    const runs = measuredSegments();
    const totalMetres = runs.reduce((sum, run) => sum + run.metres, 0);
    const alternateUnit = state.referenceUnit === 'ft' ? 'm' : 'ft';
    totalPrimary.textContent = formatLength(totalMetres);
    totalSecondary.textContent = formatLength(totalMetres, alternateUnit);
    $('[data-allowance]').textContent = formatLength(totalMetres * 1.1);
    $('[data-run-count]').textContent = String(runs.length);
    $('[data-scale-status]').textContent = calibration('front') ? `${state.referenceValue} ${state.referenceUnit}` : 'Not set';
    $('[data-depth-scale-status]').textContent = calibration('depth') ? `${state.depthValue} ${state.depthUnit}` : 'Optional';
    runList.replaceChildren();
    if (!runs.length) {
      const item = document.createElement('li');
      item.className = 'is-empty';
      item.textContent = calibration('front') ? 'Trace at least two points to measure your first run.' : 'Set the front-facing reference to begin.';
      runList.append(item);
    } else {
      runs.forEach((run, index) => {
        const item = document.createElement('li');
        const label = document.createElement('span');
        const value = document.createElement('strong');
        label.innerHTML = `Run ${index + 1} <small class="${run.scale === 'depth' ? 'is-depth' : ''}">${run.scale === 'depth' ? 'Depth' : 'Front'}</small>`;
        value.textContent = formatLength(run.metres);
        item.append(label, value);
        runList.append(item);
      });
    }
  }

  function updateUI() {
    const scaleReady = Boolean(calibration('front'));
    const segment = activeSegment();
    $('[data-undo]').disabled = history.length < 2;
    $('[data-delete-point]').disabled = !state.selected;
    $('[data-finish-run]').disabled = state.mode !== 'trace' || !segment || segment.points.length < 2;
    $('[data-new-run]').disabled = state.mode !== 'trace' || !scaleReady || !state.segments.some((entry) => entry.points.length > 1);

    root.querySelectorAll('[data-run-scale]').forEach((button) => {
      const active = button.dataset.runScale === state.activeScale;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    if (state.mode === 'reference') {
      if (!state.referencePoints.length) {
        stageHint.hidden = false;
        stageHint.innerHTML = '<strong>Set your scale</strong><span>Tap the two ends of the known distance.</span>';
        stageStatus.textContent = 'Tap the first endpoint of your known distance.';
      } else if (state.referencePoints.length === 1) {
        stageHint.hidden = false;
        stageHint.innerHTML = '<strong>One more point</strong><span>Tap the other end of the known distance.</span>';
        stageStatus.textContent = 'Now tap the second endpoint.';
      } else if (!state.referenceValue) {
        stageHint.hidden = false;
        stageHint.innerHTML = '<strong>Enter the real distance</strong><span>Use the field above the photo.</span>';
        stageStatus.textContent = 'The reference points are set. Enter their actual distance.';
      } else {
        stageHint.hidden = true;
        stageStatus.textContent = 'Front scale set. Add the optional depth scale for projecting eaves, or choose Trace roofline.';
      }
    } else if (state.mode === 'depth') {
      if (!state.depthPoints.length) {
        stageHint.hidden = false;
        stageHint.innerHTML = '<strong>Set the depth scale</strong><span>Tap both ends of a known toward-or-away distance.</span>';
        stageStatus.textContent = 'Tap the first endpoint of a known depth distance.';
      } else if (state.depthPoints.length === 1) {
        stageHint.hidden = false;
        stageHint.innerHTML = '<strong>One more depth point</strong><span>Tap the other end of the same distance.</span>';
        stageStatus.textContent = 'Now tap the second depth endpoint.';
      } else if (!state.depthValue) {
        stageHint.hidden = false;
        stageHint.innerHTML = '<strong>Enter the depth distance</strong><span>Use the field above the photo.</span>';
        stageStatus.textContent = 'The depth points are set. Enter their actual distance.';
      } else {
        stageHint.hidden = true;
        stageStatus.textContent = 'Depth scale set. Drag either endpoint to fine-tune it, then choose Trace roofline.';
      }
    } else {
      const tracedPoints = state.segments.reduce((sum, entry) => sum + entry.points.length, 0);
      stageHint.hidden = tracedPoints > 0;
      if (!tracedPoints) stageHint.innerHTML = '<strong>Trace your first eave</strong><span>Tap each corner or change in direction.</span>';
      const scaleName = state.activeScale === 'depth' ? 'depth' : 'front';
      stageStatus.textContent = segment?.points.length
        ? `Keep tapping along this ${scaleName}-facing portion. Finish the run where its direction changes.`
        : `Using the ${scaleName} scale. Tap one end of an eave to begin a roofline run.`;
    }
    updateResults();
  }

  function render() {
    renderDrawing();
    updateUI();
  }

  function setImage(source, width, height) {
    state.imageWidth = width;
    state.imageHeight = height;
    state.referencePoints = [];
    state.referenceValue = 0;
    state.referenceUnit = 'ft';
    state.depthPoints = [];
    state.depthValue = 0;
    state.depthUnit = 'ft';
    state.segments = [];
    state.activeSegmentId = null;
    state.selected = null;
    state.mode = 'reference';
    state.activeScale = 'front';
    referenceInput.value = '';
    referenceUnit.value = 'ft';
    depthInput.value = '';
    depthUnit.value = 'ft';
    image.src = source;
    stage.style.aspectRatio = `${width} / ${height}`;
    uploadView.hidden = true;
    workspace.hidden = false;
    history = [];
    pushHistory();
    setMode('reference');
    requestAnimationFrame(render);
    workspace.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function processFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      uploadMessage.textContent = 'Please choose a JPEG, PNG, WebP, HEIC, or HEIF image.';
      return;
    }
    uploadMessage.textContent = 'Preparing your photo…';
    try {
      const bitmap = await createImageBitmap(file);
      const maxDimension = 2400;
      const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
      const width = Math.round(bitmap.width * scale);
      const height = Math.round(bitmap.height * scale);
      const processor = document.createElement('canvas');
      processor.width = width;
      processor.height = height;
      processor.getContext('2d', { alpha: false }).drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      const source = processor.toDataURL('image/jpeg', 0.9);
      uploadMessage.textContent = width < 1000 || height < 600 ? 'A larger photo will make fine tracing more accurate.' : '';
      setImage(source, width, height);
      track('photo_added', { source: 'upload' });
    } catch (_error) {
      uploadMessage.textContent = 'This browser could not open that photo. Try saving it as a JPEG first.';
    }
  }

  async function openCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      uploadMessage.textContent = 'Camera access is unavailable here. Choose an existing photo instead.';
      return;
    }
    try {
      cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      cameraVideo.srcObject = cameraStream;
      updateCameraLayout();
      cameraDialog.showModal();
    } catch (_error) {
      uploadMessage.textContent = 'Camera access was unavailable. You can still choose an existing photo.';
    }
  }

  function closeCamera() {
    if (cameraStream) cameraStream.getTracks().forEach((track) => track.stop());
    cameraStream = null;
    cameraVideo.srcObject = null;
    if (cameraDialog.open) cameraDialog.close();
  }

  function updateCameraLayout() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const portrait = window.matchMedia('(orientation: portrait)').matches;
    cameraDialog.style.setProperty('--camera-viewport-height', `${Math.round(viewportHeight)}px`);
    $('[data-camera-orientation]').hidden = !portrait;
  }

  function capturePhoto() {
    if (!cameraVideo.videoWidth) return;
    const maxDimension = 2400;
    const scale = Math.min(1, maxDimension / Math.max(cameraVideo.videoWidth, cameraVideo.videoHeight));
    const processor = document.createElement('canvas');
    processor.width = Math.round(cameraVideo.videoWidth * scale);
    processor.height = Math.round(cameraVideo.videoHeight * scale);
    processor.getContext('2d', { alpha: false }).drawImage(cameraVideo, 0, 0, processor.width, processor.height);
    const source = processor.toDataURL('image/jpeg', 0.9);
    closeCamera();
    setImage(source, processor.width, processor.height);
    track('photo_added', { source: 'camera' });
  }

  function resetAll() {
    closeCamera();
    state.imageWidth = 0;
    state.imageHeight = 0;
    state.referencePoints = [];
    state.referenceValue = 0;
    state.referenceUnit = 'ft';
    state.depthPoints = [];
    state.depthValue = 0;
    state.depthUnit = 'ft';
    state.activeScale = 'front';
    state.segments = [];
    state.activeSegmentId = null;
    state.selected = null;
    state.mode = 'reference';
    referenceInput.value = '';
    referenceUnit.value = 'ft';
    depthInput.value = '';
    depthUnit.value = 'ft';
    photoInput.value = '';
    image.removeAttribute('src');
    history = [];
    workspace.hidden = true;
    uploadView.hidden = false;
    uploadMessage.textContent = '';
    if (confirmDialog.open) confirmDialog.close();
    uploadView.scrollIntoView({ behavior: 'smooth', block: 'start' });
    track('started_over');
  }

  drawingLayer.addEventListener('pointerdown', (event) => {
    const handle = event.target.closest('[data-kind]');
    const segmentLine = event.target.closest('[data-segment-line]');
    if (handle) {
      event.preventDefault();
      event.stopPropagation();
      dragging = {
        kind: handle.dataset.kind,
        pointIndex: Number(handle.dataset.pointIndex),
        segmentId: handle.dataset.segmentId || null,
        pointerId: event.pointerId
      };
      state.selected = { kind: dragging.kind, pointIndex: dragging.pointIndex, segmentId: dragging.segmentId };
      drawingLayer.setPointerCapture(event.pointerId);
      render();
      return;
    }
    if (segmentLine) {
      event.stopPropagation();
      state.activeSegmentId = segmentLine.dataset.segmentLine;
      state.activeScale = state.segments.find((segment) => segment.id === state.activeSegmentId)?.scale || 'front';
      state.selected = null;
      render();
      return;
    }
    addPoint(event);
  });

  drawingLayer.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const point = stagePoint(event);
    if (dragging.kind === 'reference') state.referencePoints[dragging.pointIndex] = point;
    else if (dragging.kind === 'depth') state.depthPoints[dragging.pointIndex] = point;
    else {
      const segment = state.segments.find((entry) => entry.id === dragging.segmentId);
      if (segment?.points[dragging.pointIndex]) segment.points[dragging.pointIndex] = point;
    }
    render();
  });

  function endDrag(event) {
    if (!dragging) return;
    if (drawingLayer.hasPointerCapture(event.pointerId)) drawingLayer.releasePointerCapture(event.pointerId);
    dragging = null;
    pushHistory();
  }

  drawingLayer.addEventListener('pointerup', endDrag);
  drawingLayer.addEventListener('pointercancel', endDrag);
  photoInput.addEventListener('change', (event) => processFile(event.target.files?.[0]));
  referenceInput.addEventListener('input', () => {
    state.referenceValue = Math.max(0, Number(referenceInput.value) || 0);
    render();
  });
  referenceInput.addEventListener('change', () => track('reference_value_set', { unit: state.referenceUnit }));
  referenceUnit.addEventListener('change', () => {
    state.referenceUnit = referenceUnit.value;
    render();
  });
  depthInput.addEventListener('input', () => {
    state.depthValue = Math.max(0, Number(depthInput.value) || 0);
    render();
  });
  depthInput.addEventListener('change', () => track('depth_value_set', { unit: state.depthUnit }));
  depthUnit.addEventListener('change', () => {
    state.depthUnit = depthUnit.value;
    render();
  });
  root.querySelectorAll('[data-mode-button]').forEach((button) => button.addEventListener('click', () => setMode(button.dataset.modeButton)));
  $('[data-reset-reference]').addEventListener('click', () => {
    state.referencePoints = [];
    state.segments = [];
    state.activeSegmentId = null;
    state.selected = null;
    pushHistory();
    render();
  });
  $('[data-reset-depth]').addEventListener('click', () => {
    state.depthPoints = [];
    state.segments = state.segments.filter((segment) => segment.scale !== 'depth');
    if (!state.segments.some((segment) => segment.id === state.activeSegmentId)) state.activeSegmentId = null;
    if (state.activeScale === 'depth') state.activeScale = 'front';
    state.selected = null;
    pushHistory();
    render();
  });
  root.querySelectorAll('[data-run-scale]').forEach((button) => button.addEventListener('click', () => {
    const segment = activeSegment();
    if (segment?.points.length) {
      stageStatus.textContent = 'Finish the current run before changing its scale.';
      return;
    }
    if (button.dataset.runScale === 'depth' && !calibration('depth')) {
      setMode('depth');
      stageStatus.textContent = 'Set the optional depth reference before tracing a depth-facing run.';
      if (!(state.depthValue > 0)) depthInput.focus();
      return;
    }
    state.activeScale = button.dataset.runScale;
    if (segment) segment.scale = state.activeScale;
    render();
  }));
  $('[data-undo]').addEventListener('click', undo);
  $('[data-delete-point]').addEventListener('click', deleteSelectedPoint);
  $('[data-finish-run]').addEventListener('click', () => finishRun(false));
  $('[data-new-run]').addEventListener('click', () => finishRun(true));
  $('[data-jump-to-tool]').addEventListener('click', () => $('[data-tool-anchor]').scrollIntoView({ behavior: 'smooth', block: 'start' }));
  $('[data-replace-photo]').addEventListener('click', () => {
    workspace.hidden = true;
    uploadView.hidden = false;
    uploadView.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  $('[data-start-over]').addEventListener('click', () => confirmDialog.showModal());
  $('[data-cancel-reset]').addEventListener('click', () => confirmDialog.close());
  $('[data-confirm-reset]').addEventListener('click', resetAll);
  $('[data-open-camera]').addEventListener('click', openCamera);
  $('[data-close-camera]').addEventListener('click', closeCamera);
  $('[data-capture-photo]').addEventListener('click', capturePhoto);
  cameraDialog.addEventListener('close', closeCamera);
  window.addEventListener('resize', render);
  window.visualViewport?.addEventListener('resize', updateCameraLayout);
  window.addEventListener('orientationchange', () => window.setTimeout(updateCameraLayout, 150));
  root.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      undo();
    }
  });

  render();
  track('page_viewed');
}
